/**
 * INSULATION SCHEDULE round (2026-10-03) — the insulation SPEC vocabulary: kinds, the shared
 * type-param group, and the per-type layer reader. Split from insulation.ts (which reads the model)
 * so the category schemas can import it with no path back into doc.ts (no load-order cycle).
 */
import type { ParamDef } from './params'
import type { ParamValue } from './types'

export interface InsulationKind {
  value: string
  label: string
  /** upper-end R per inch, for the fits-in-the-cavity check; null = not in the cavity */
  rPerInMax: number | null
}

export const INSULATION_KINDS: InsulationKind[] = [
  { value: 'none', label: 'None', rPerInMax: null },
  { value: 'batt', label: 'Fiberglass Batt', rPerInMax: 3.8 },          // high-density fiberglass
  { value: 'mineralWool', label: 'Mineral Wool Batt', rPerInMax: 4.3 },
  { value: 'ccSpray', label: 'Closed-Cell Spray Foam', rPerInMax: 7.0 },
  { value: 'ocSpray', label: 'Open-Cell Spray Foam', rPerInMax: 3.8 },
  { value: 'blown', label: 'Blown-In Loose Fill', rPerInMax: 3.7 },
  { value: 'rigid', label: 'Rigid Board (continuous)', rPerInMax: null }, // outside the framing
]
const KIND = Object.fromEntries(INSULATION_KINDS.map((k) => [k.value, k]))

const kindChoices = INSULATION_KINDS.map((k) => ({ value: k.value, label: k.label }))

/** The shared "Insulation" type-param group — added to wall / roof / floor / ceiling schemas. */
export const INSULATION_PARAM_GROUP: { label: string; params: ParamDef[] } = {
  label: 'Insulation',
  params: [
    { key: 'insulationType1', label: 'Insulation', kind: 'choice', default: 'none', choices: kindChoices },
    {
      key: 'insulationR1', label: 'R-Value', kind: 'number', default: null, optional: true, optionalHint: 'not set', min: 0, max: 100,
      conditionalOn: { param: 'insulationType1', equals: INSULATION_KINDS.filter((k) => k.value !== 'none').map((k) => k.value) },
    },
    {
      key: 'insulationType2', label: 'Second Layer', kind: 'choice', default: 'none', choices: kindChoices,
      conditionalOn: { param: 'insulationType1', equals: INSULATION_KINDS.filter((k) => k.value !== 'none').map((k) => k.value) },
    },
    {
      key: 'insulationR2', label: 'Second Layer R-Value', kind: 'number', default: null, optional: true, optionalHint: 'not set', min: 0, max: 100,
      conditionalOn: [
        { param: 'insulationType1', equals: INSULATION_KINDS.filter((k) => k.value !== 'none').map((k) => k.value) },
        { param: 'insulationType2', equals: INSULATION_KINDS.filter((k) => k.value !== 'none').map((k) => k.value) },
      ],
    },
  ],
}

export interface InsulationLayer { kind: string; label: string; r: number | null }

/** The type's specified insulation layers (none → []). */
export function insulationLayers(typeParams: Record<string, ParamValue>): InsulationLayer[] {
  const out: InsulationLayer[] = []
  for (const n of [1, 2]) {
    const kind = String(typeParams[`insulationType${n}`] ?? 'none')
    if (kind === 'none' || !KIND[kind]) { if (n === 1) break; continue }
    const r = typeParams[`insulationR${n}`]
    out.push({ kind, label: KIND[kind].label, r: typeof r === 'number' && Number.isFinite(r) ? r : null })
  }
  return out
}

/** "R-19 Mineral Wool Batt + R-5 Closed-Cell Spray Foam" — the wall schedule's R column etc. */
export function insulationSummary(typeParams: Record<string, ParamValue>): string {
  return insulationLayers(typeParams).map((l) => `${l.r == null ? 'R-?' : `R-${l.r}`} ${l.label}`).join(' + ')
}
